import { CardEligibility } from './card-eligibility.js';
let MoonExchangeData = {};

MoonExchangeData = (function () {
  function isExchangeListable(card) {
    if (!card) return false;
    if (CardEligibility?.isExchangeListableCard) {
      return CardEligibility.isExchangeListableCard(card);
    }
    const type = String(card.type || '').toLowerCase();
    if (type !== 'character') return false;
    if (card.isForged === true) return true;
    if (String(card.collection || '') === 'Moon Foundry') return true;
    return false;
  }

  function toNumber(value, fallback = 0) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
  }

  function calculateFee(totalPrice, feeRate) {
    const total = toNumber(totalPrice, 0);
    const rate = toNumber(feeRate, 0);
    if (total <= 0 || rate <= 0) return 0;
    return Math.max(0, total - Math.floor(total * (1 - rate)));
  }

  function calculateNet(totalPrice, feeRate) {
    const total = toNumber(totalPrice, 0);
    if (total <= 0) return 0;
    const fee = calculateFee(total, feeRate);
    return Math.max(0, Math.floor(total - fee));
  }

  function buildSellableCards({ allCards = [], ownedCardMap, ownedCardStellarMap } = {}) {
    if (!ownedCardMap || typeof ownedCardMap.get !== 'function') return [];
    const stellarMap = ownedCardStellarMap instanceof Map ? ownedCardStellarMap : new Map();

    const entries = allCards
      .flatMap((card) => {
        if (!isExchangeListable(card)) return [];
        const baseName = card.name || card.id;
        const results = [];
        const normalQty = toNumber(ownedCardMap.get(card.id), 0);
        if (normalQty > 0) {
          results.push({
            cardId: card.id,
            variant: 'normal',
            isStellar: false,
            name: baseName,
            baseName,
            image: card.image || null,
            description: card.description || '',
            collection: card.collection || 'Classic',
            cost: toNumber(card.cost, 0),
            type: card.type || 'Character',
            character: card.character,
            targetingType: card.targetingType,
            available: normalQty,
          });
        }
        const stellarQty = toNumber(stellarMap.get(card.id), 0);
        if (stellarQty > 0) {
          results.push({
            cardId: card.id,
            variant: 'stellar',
            isStellar: true,
            name: `${baseName} (Stellar)`,
            baseName,
            image: card.image || null,
            description: card.description || '',
            collection: card.collection || 'Classic',
            cost: toNumber(card.cost, 0),
            type: card.type || 'Character',
            character: card.character,
            targetingType: card.targetingType,
            available: stellarQty,
          });
        }
        return results;
      })
      .filter(Boolean);

    entries.sort((a, b) => {
      const nameCompare = a.baseName.localeCompare(b.baseName);
      if (nameCompare !== 0) return nameCompare;
      if (a.variant === b.variant) return 0;
      return a.variant === 'normal' ? -1 : 1;
    });
    return entries;
  }

  /**
   * @param {object} [options]
   * @param {Array<object>} [options.listings] listing rows as the server sends them
   * @param {Array<object>} [options.allCards] the cards this client holds, in
   *   the reader's language (the catalogue, or the page's details)
   * @param {string|null} [options.currentUserId]
   * @param {string} [options.stellarLabel] the word for a Stellar print, in
   *   the reader's language
   */
  function buildListingView({ listings = [], allCards = [], currentUserId = null, stellarLabel = 'Stellar' } = {}) {
    const cardById = new Map(allCards.map((card) => [card.id, card]));

    const view = listings.map((listing) => {
      const card = cardById.get(listing.cardId) || null;
      const unitPrice = toNumber(listing.unitPrice, 0);
      const quantityRemaining = toNumber(listing.quantityRemaining, 0);
      const isStellar = listing.isStellar === true;
      /* THE CARD'S OWN CURRENT WORDS, IN THE READER'S LANGUAGE (28 September
         2026). A row's `cardName`/`cardDescription` are the listing's
         snapshot, stored in English when the card was listed: measured on dev
         against the Japanese catalogue, 13 of 13 rows differed, and one was
         stale in English too. The card this client holds (the catalogue, or
         the page's details) names the row, exactly as it draws the row's
         face; the snapshot is the last resort — a card deleted since it was
         listed — and never the raw `cardId` before it (a card forged after
         the catalogue loaded once rendered its id with no rules text). */
      const baseName = card?.name || listing.cardName || listing.cardId || 'Unknown Card';
      return {
        id: listing.id,
        cardId: listing.cardId,
        variant: isStellar ? 'stellar' : 'normal',
        isStellar,
        cardName: isStellar ? `${baseName} (${stellarLabel})` : baseName,
        // The card's own name, with no print word: the listing view and its face.
        cardBaseName: baseName,
        cardDescription: card?.description || listing.cardDescription || '',
        manaCost: toNumber(listing.manaCost, card?.manaCost ?? 0),
        isSpell: listing.isSpell === true || card?.type === 'Spell',
        /* THE ROW'S OWN ART FIRST (read audit F3, 28 September 2026): the URL
           the collection serves for the card, from the card read the row
           already makes (`listOpenListings` `cardImage`), so a row paints
           with no catalogue. An older row without it takes the catalogue's. */
        cardImage: (typeof listing.cardImage === 'string' && listing.cardImage) || card?.image || '',
        unitPrice,
        quantityTotal: toNumber(listing.quantityTotal, quantityRemaining),
        quantityRemaining,
        status: listing.status || 'open',
        sellerId: listing.sellerId || null,
        sellerName: listing.sellerName || 'Player',
        isMine: listing.isMine === true || (currentUserId ? listing.sellerId === currentUserId : false),
        totalPrice: unitPrice * quantityRemaining,
        createdAt: listing.createdAt || null,
      };
    });

    return view;
  }

  function filterListingsBySearch({ listings = [], allCards = [], searchText = '' } = {}) {
    const term = String(searchText || '').trim().toLowerCase();
    if (!term) return listings;

    const cardById = new Map(allCards.map((card) => [card.id, card]));

    return listings.filter((listing) => {
      const card = cardById.get(listing.cardId);
      const name = String(card?.name || listing.cardId || '').toLowerCase();
      const description = String(card?.description || '').toLowerCase();
      return name.includes(term) || description.includes(term);
    });
  }

  return {
    calculateFee,
    calculateNet,
    buildSellableCards,
    buildListingView,
    filterListingsBySearch,
  };
})();

export { MoonExchangeData };
